import { useState } from 'react';
import { useTickets } from '@/hooks/useTickets';
import { useAuth } from '@/hooks/useAuth';
import { useLeadDepartment } from '@/hooks/useConfig';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PriorityBadge } from '@/components/ui/priority-badge';
import { StageBadge } from '@/components/ui/stage-badge';
import { DaysSinceSubmissionBadge, getDaysSinceSubmission } from '@/components/ui/days-since-badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { format } from 'date-fns';
import { Search, Filter, Eye, ArrowUpDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { TicketPriority } from '@/lib/supabase-types';

export function TicketsListPage() {
  const { isTeamLeader } = useAuth();
  const { leadDepartmentId } = useLeadDepartment();
  const ticketFilters = isTeamLeader && leadDepartmentId ? { departmentId: leadDepartmentId } : undefined;
  const { tickets, stages, isLoading } = useTickets(ticketFilters);
  const [search, setSearch] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [stageFilter, setStageFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'created_at' | 'priority'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const filteredTickets = tickets
    .filter((ticket) => {
      const matchesSearch =
        ticket.ticket_number?.toLowerCase().includes(search.toLowerCase()) ||
        ticket.requester_name?.toLowerCase().includes(search.toLowerCase()) ||
        ticket.requester_email?.toLowerCase().includes(search.toLowerCase()) ||
        ticket.description?.toLowerCase().includes(search.toLowerCase());
      
      const matchesPriority = priorityFilter === 'all' || ticket.priority === priorityFilter;
      const matchesStage = stageFilter === 'all' || ticket.stage_id === stageFilter;

      return matchesSearch && matchesPriority && matchesStage;
    })
    .sort((a, b) => {
      if (sortBy === 'created_at') {
        const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
      }
      
      const priorityOrder: Record<TicketPriority, number> = {
        critical: 4,
        high: 3,
        medium: 2,
        low: 1,
      };
      const orderA = priorityOrder[a.priority];
      const orderB = priorityOrder[b.priority];
      return sortOrder === 'asc' ? orderA - orderB : orderB - orderA;
    });

  const toggleSort = (field: 'created_at' | 'priority') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-foreground">All Tickets</h1>
        <p className="text-muted-foreground">View and manage all support tickets</p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search tickets..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex gap-3">
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-[140px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
              <Select value={stageFilter} onValueChange={setStageFilter}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Stage" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stages</SelectItem>
                  {stages.map((stage) => (
                    <SelectItem key={stage.id} value={stage.id}>
                      {stage.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tickets Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ticket</TableHead>
                <TableHead>Requester</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleSort('priority')}
                    className="-ml-3"
                  >
                    Priority
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                  </Button>
                </TableHead>
                <TableHead>Stage</TableHead>
                <TableHead>Assignees</TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleSort('created_at')}
                    className="-ml-3"
                  >
                    Created
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                  </Button>
                </TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTickets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No tickets found
                  </TableCell>
                </TableRow>
              ) : (
                filteredTickets.map((ticket) => (
                  <TableRow key={ticket.id}>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium">{ticket.ticket_number}</p>
                          <DaysSinceSubmissionBadge days={getDaysSinceSubmission(ticket)} />
                        </div>
                        <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                          {ticket.description}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm">{ticket.requester_name}</p>
                        <p className="text-xs text-muted-foreground">{ticket.requester_email}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {ticket.ticket_type?.name}
                    </TableCell>
                    <TableCell>
                      <PriorityBadge priority={ticket.priority} showIcon={false} />
                    </TableCell>
                    <TableCell>
                      {ticket.stage && <StageBadge stage={ticket.stage} />}
                    </TableCell>
                    <TableCell>
                      {ticket.assignees && ticket.assignees.length > 0 ? (
                        <TooltipProvider delayDuration={300}>
                          <div className="flex -space-x-2">
                            {ticket.assignees.slice(0, 3).map((assignee) => {
                              const name = assignee.profile?.name ?? assignee.user?.profile?.name ?? 'Unknown';
                              return (
                                <Tooltip key={assignee.id}>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex">
                                      <Avatar className="h-7 w-7 border-2 border-background cursor-default">
                                        <AvatarImage src={assignee.profile?.avatar_url ?? assignee.user?.profile?.avatarUrl ?? undefined} />
                                        <AvatarFallback className="text-xs">
                                          {name.charAt(0)}
                                        </AvatarFallback>
                                      </Avatar>
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="top">
                                    {name}
                                  </TooltipContent>
                                </Tooltip>
                              );
                            })}
                            {ticket.assignees.length > 3 && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center text-xs border-2 border-background cursor-default">
                                    +{ticket.assignees.length - 3}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                  {ticket.assignees.slice(3).map((a) => a.profile?.name ?? a.user?.profile?.name ?? 'Unknown').join(', ')}
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </TooltipProvider>
                      ) : (
                        <span className="text-xs text-muted-foreground">Unassigned</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {ticket.created_at 
                        ? format(new Date(ticket.created_at), 'MMM d, yyyy')
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Link to={`/admin/tickets/${ticket.id}`}>
                        <Button variant="ghost" size="icon">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}